import React, { useEffect, useState } from "react"
import { Card, Statistic, Button, Modal, Space, Row, Col, Divider, Typography, Alert, List, Empty, Spin } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMoneyBillWave, faWallet, faCheckCircle, faDownload, faEye, faHourglassHalf } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import axios from "../../../api/axios"
import RibModal from "./RibModal"
import { openFactureInNewTab, downloadFacture } from "../../../services/Avoir_service"
import { CreditNoteType } from "../../../static/types/creditNoteType"

const { Title, Text } = Typography

interface UserAvoirData {
  name: string
  email: string
  soldePending: number
  montantARembourser: number
  montantRembourse: number
  countPending: number
  countARembourser: number
  countRembourse: number
  avoirsDetails: {
    pending: CreditNoteType[]
    aRembourser: CreditNoteType[]
    rembourse: CreditNoteType[]
  }
  // optional: per-user override rate (percent)
  payment_link_credit_percentage?: number | null
}

const CreditNote: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [userData, setUserData] = useState<UserAvoirData | null>(null)
  const [globalPercentage, setGlobalPercentage] = useState<number | null>(null)
  const [ribModalVisible, setRibModalVisible] = useState<boolean>(false)
  const [ribModalLoading, setRibModalLoading] = useState<boolean>(false)
  const [detailsModalVisible, setDetailsModalVisible] = useState<boolean>(false)
  const [modalView, setModalView] = useState<'details' | 'factures'>('details')
  const [montantRemboursement, setMontantRemboursement] = useState<number>(0)

  const userId = localStorage.getItem("userId")

  const primaryColor = '#2A919D'
  const secondaryColor = '#4BB0C0'
  const accentColor = '#eb8635'

  useEffect(() => {
  if (userId) {
    fetchGlobalRate()
    fetchUserAvoirs()

    // ✅ AJOUT : Rafraîchir toutes les 10 secondes
    const interval = setInterval(() => {
      fetchUserAvoirs()
    }, 10000) // 10 secondes

    // ✅ AJOUT : Rafraîchir quand la fenêtre reprend le focus
    const onFocus = () => {
      fetchUserAvoirs()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUserAvoirs()
      }
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }
}, [userId])

  const fetchGlobalRate = async (): Promise<void> => {
    try {
      const resp = await axios.get("/avoirs/payment-link-rate")
      const pct = resp.data?.percentage
      setGlobalPercentage(pct != null ? Number(pct) : null)
    } catch (err) {
      console.error("Erreur récupération taux global:", err)
      setGlobalPercentage(null)
    }
  }

 const fetchUserAvoirs = async (): Promise<void> => {
  setLoading(true)
  try {
    const response = await axios.get("/avoirs/get-avoirs")
    const avoirs: CreditNoteType[] = response.data.data || []

    const userAvoirs = avoirs.filter((avoir: CreditNoteType) =>
      avoir.annonce?.User?.id?.toString() === userId
    )

    const data: UserAvoirData = {
      name: (userAvoirs[0]?.annonce?.User?.firstname ?? '') + ' ' + (userAvoirs[0]?.annonce?.User?.name ?? '') || 'Client',
      email: userAvoirs[0]?.annonce?.User?.email || '',
      soldePending: 0, // ⚠️ Calculé à partir des avoirs, PAS de user.avoir_balance
      montantARembourser: 0,
      montantRembourse: 0,
      countPending: 0,
      countARembourser: 0,
      countRembourse: 0,
      avoirsDetails: {
        pending: [],
        aRembourser: [],
        rembourse: []
      },
      payment_link_credit_percentage: userAvoirs[0]?.annonce?.User?.payment_link_credit_percentage != null
        ? Number(userAvoirs[0].annonce.User.payment_link_credit_percentage)
        : null
    }

    userAvoirs.forEach((avoir: CreditNoteType) => {
      const montant = Number(avoir.montant_avoir) || 0

      if (avoir.statut === 'pending') {
        data.soldePending += montant // ✅ Somme des avoirs "pending"
        data.countPending++
        data.avoirsDetails.pending.push(avoir)
      } else if (avoir.statut === 'à_rembourser') {
        data.montantARembourser += montant
        data.countARembourser++
        data.avoirsDetails.aRembourser.push(avoir)
      } else if (avoir.statut === 'remboursé') {
        data.montantRembourse += montant
        data.countRembourse++
        data.avoirsDetails.rembourse.push(avoir)
      }
      // ✅ NE PAS compter les avoirs "utilisés"
    })

    setUserData(data)
    console.log('[CREDIT_NOTE] ✅ Solde rafraîchi:', data.soldePending.toFixed(2), '€')
  } catch (error) {
    console.error("Erreur:", error)
    toast.error("Erreur lors du chargement des avoirs")
  } finally {
    setLoading(false)
  }
}
  const handleDemanderRemboursement = async (): Promise<void> => {
    if (!userData?.soldePending || userData.soldePending <= 0) {
      toast.warning("Aucun avoir en attente à rembourser")
      return
    }

    const { value: montant } = await Swal.fire({
      title: 'Demander le remboursement',
      html: `
        <p><strong>Solde disponible:</strong> ${userData.soldePending.toFixed(2)} €</p>
        <hr />
        <p style="margin-top: 16px; margin-bottom: 8px; text-align: left; font-weight: bold;">Montant à rembourser (€) :</p>
      `,
      input: 'number',
      inputValue: userData.soldePending,
      inputAttributes: {
        min: '0.01',
        max: userData.soldePending.toString(),
        step: '0.001'
      },
      inputValidator: (value) => {
        const num = parseFloat(value)
        if (!value || isNaN(num)) {
          return 'Veuillez entrer un montant valide'
        }
        if (num <= 0) {
          return 'Le montant doit être supérieur à 0'
        }
        if (num > userData.soldePending) {
          return `Le montant ne peut pas dépasser ${userData.soldePending.toFixed(2)} €`
        }
        return null
      },
      showCancelButton: true,
      confirmButtonColor: secondaryColor,
      cancelButtonColor: '#dc3545',
      confirmButtonText: 'Continuer',
      cancelButtonText: 'Annuler',
      customClass: {
        input: 'swal2-input-custom'
      }
    })

    if (montant) {
      setMontantRemboursement(parseFloat(montant))
      setRibModalVisible(true)
    }
  }

  // NOTE: bic is now optional in signature but we enforce it because RibModal is opened with requireBic={true}
  const handleConfirmRib = async (rib: string, bic?: string): Promise<void> => {
    if (!userData || !userId || montantRemboursement <= 0) return

    // Vérifier que le BIC est présent (obligatoire pour ce cas)
    if (!bic) {
      toast.error("Le BIC est requis pour continuer")
      return
    }

    try {
      setRibModalLoading(true)

      await axios.post('/avoirs/rembourser-global', {
        userId: userId,
        montant: montantRemboursement,
        rib: rib,
        bic: bic
      })

      toast.success("Demande de remboursement enregistrée avec succès")
      setRibModalVisible(false)
      setMontantRemboursement(0)
      await fetchUserAvoirs()

      Swal.fire({
        title: 'Demande enregistrée !',
        html: `
          <p>Votre demande de remboursement de <strong>${montantRemboursement.toFixed(2)} €</strong> a été enregistrée.</p>
          <p style="color: #666;">Vous recevrez le remboursement sous 30 jours.</p>
        `,
        icon: 'success',
        confirmButtonColor: primaryColor
      })
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de la demande")
    } finally {
      setRibModalLoading(false)
    }
  }

  if (loading && !userData) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px', color: primaryColor }}>Chargement de vos avoirs...</p>
      </div>
    )
  }

  const totalAvoirs = (userData?.soldePending || 0) + (userData?.montantARembourser || 0) + (userData?.montantRembourse || 0)

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Title level={2} style={{ color: primaryColor, marginBottom: '8px' }}>
        <FontAwesomeIcon icon={faWallet} className="me-3" />
        Mes Avoirs
      </Title>
      <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '24px' }}>
        Consultez et gérez vos avoirs disponibles
      </Text>

      {/* Show per-user rate if present, otherwise show global rate (always prefer user override) */}
      {userData && (
        <Card style={{ marginBottom: 16 }}>
          {userData.payment_link_credit_percentage != null ? (
            <>
              <Text strong>Votre taux personnalisé pour paiements par lien &nbsp;: &nbsp;</Text>
              <Text>{userData.payment_link_credit_percentage}%</Text>
            </>
          ) : globalPercentage != null ? (
            <>
              <Text strong>Taux global pour paiements par lien &nbsp;: &nbsp;</Text>
              <Text>{globalPercentage}%</Text>
            </>
          ) : (
            <Text type="secondary">Aucun taux attribué pour les paiements par lien</Text>
          )}
        </Card>
      )}

      {totalAvoirs === 0 && (
        <Card>
          <Empty
            description="Vous n'avez aucun avoir pour le moment"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {totalAvoirs > 0 && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={8}>
              <Card hoverable style={{ borderRadius: '12px' }}>
                <Statistic
                  title={<span style={{ fontSize: '16px' }}>Solde disponible</span>}
                  value={userData?.soldePending.toFixed(2) || '0.000'}
                  suffix="€"
                  prefix={<FontAwesomeIcon icon={faHourglassHalf} />}
                  valueStyle={{ color: secondaryColor, fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card hoverable style={{ borderRadius: '12px' }}>
                <Statistic
                  title={<span style={{ fontSize: '16px' }}>En cours de remboursement</span>}
                  value={userData?.montantARembourser.toFixed(2) || '0.000'}
                  suffix="€"
                  prefix={<FontAwesomeIcon icon={faMoneyBillWave} />}
                  valueStyle={{ color: accentColor, fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card hoverable style={{ borderRadius: '12px' }}>
                <Statistic
                  title={<span style={{ fontSize: '16px' }}>Remboursé</span>}
                  value={userData?.montantRembourse.toFixed(2) || '0.000'}
                  suffix="€"
                  prefix={<FontAwesomeIcon icon={faCheckCircle} />}
                  valueStyle={{ color: primaryColor, fontWeight: 'bold', fontSize: '24px' }}
                />
              </Card>
            </Col>
          </Row>

          {userData && userData.soldePending > 0 && (
            <Card
              style={{
                marginBottom: '24px',
                backgroundColor: '#fff7e6',
                borderRadius: '12px',
                border: '1px solid #ffd591'
              }}
            >
              <Row justify="space-between" align="middle">
                <Col xs={24} md={16} >
                  <Title level={5} style={{ marginBottom: '8px', color: accentColor, fontSize: '18px' }}>
                    <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                    Solde disponible
                  </Title>
                  <Text style={{ fontSize: '16px'}}>
                    Vous avez {userData.soldePending.toFixed(2)} € d'avoirs disponibles.
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '14px' }}>
                    Demandez le remboursement pour recevoir le montant sous 30 jours
                  </Text>
                </Col>
                <Col xs={24} md={8} style={{ textAlign: 'right', marginTop: '16px' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    onClick={handleDemanderRemboursement}
                    style={{ backgroundColor: secondaryColor, borderColor: secondaryColor,fontWeight: 'bold', fontSize: '16px' }}
                    loading={loading}
                  >
                    Demander le remboursement
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          <Row gutter={[16, 16]}>
            {userData && (
              <Col xs={24} md={12}>
                <Card
                  title={
                    <span style={{ color: accentColor, fontSize: '20px' }}>
                      <FontAwesomeIcon icon={faHourglassHalf} className="me-2" />
                      En cours de remboursement
                    </span>
                  }
                  style={{ borderRadius: '12px' }}
                >
                  <div
                    style={{
                      padding: '14px',
                      borderRadius: 8,
                      border: userData.montantARembourser > 0 ? '1px solid #ffd591' : '1px solid #e6f7ff',
                      backgroundColor: userData.montantARembourser > 0 ? '#fff7e6' : '#f0f7ff',
                      marginBottom: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8
                    }}
                  >
                    {userData.montantARembourser > 0 ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FontAwesomeIcon icon={faHourglassHalf} style={{ color: accentColor }} />
                          <Text strong style={{ fontSize: '18px', color: accentColor }}>
                            {userData.montantARembourser.toFixed(2)} € en cours
                          </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                          {userData.countARembourser} avoir{userData.countARembourser > 1 ? 's' : ''} en cours — remboursé{userData.countARembourser > 1 ? 's' : ''} sous 30 jours
                        </Text>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FontAwesomeIcon icon={faHourglassHalf} style={{ color: primaryColor }} />
                          <Text strong style={{ fontSize: '18px', color: primaryColor }}>
                            Aucun remboursement en cours
                          </Text>
                        </div>

                        <div style={{ display: 'flex', gap: 10, marginTop: -5 }}>
                          <Button
                            type="primary"
                            onClick={handleDemanderRemboursement}
                            disabled={!userData.soldePending || userData.soldePending <= 0}
                            icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                            style={{
                              backgroundColor: secondaryColor,
                              borderColor: secondaryColor,
                              flex: 1,
                              fontWeight: 'bold',
                              fontSize: '16px'
                            }}
                          >
                            Demander le remboursement
                          </Button>

                          <div style={{ minWidth: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text type="secondary" style={{ fontSize: '15px', textAlign: 'center' }}>
                              {(!userData.soldePending || userData.soldePending <= 0) ? 'Aucun avoir disponible' : `${userData.soldePending.toFixed(2)} € disponibles`}
                            </Text>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <Button
                    block
                    icon={<FontAwesomeIcon icon={faEye} />}
                    onClick={() => { setModalView('details'); setDetailsModalVisible(true); }}
                    style={{
                      background: '#fff',
                      borderColor: '#d9d9d9',
                      color: '#000'
                    }}
                  >
                    Voir les détails
                  </Button>
                </Card>
              </Col>
            )}

            {userData && (
              <Col xs={24} md={12}>
                <Card
                  title={
                    <span style={{ color: primaryColor, fontSize: '20px' }}>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Avoirs remboursés
                    </span>
                  }
                  style={{ borderRadius: '12px' }}
                >
                  <div
                    style={{
                      padding: '16px',
                      borderRadius: 8,
                      border: '1px solid #dff7e6',
                      backgroundColor: '#f6ffed',
                      marginBottom: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8
                    }}
                  >
                    {userData.montantRembourse > 0 ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: primaryColor }} />
                          <Text strong style={{ fontSize: 16, color: primaryColor }}>
                            {userData.montantRembourse.toFixed(2)} € remboursé
                          </Text>
                        </div>
                        <Text type="secondary">
                          {userData.countRembourse} facture{userData.countRembourse > 1 ? 's' : ''} disponible{userData.countRembourse > 1 ? 's' : ''}
                        </Text>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ color: primaryColor }} />
                          <Text strong style={{ fontSize: '18px', color: primaryColor }}>
                            Aucun avoir remboursé
                          </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px' }}>Vous n'avez aucune facture remboursée pour le moment.</Text>
                      </>
                    )}
                  </div>

                  <Button
                    block
                    icon={<FontAwesomeIcon icon={faEye} />}
                    onClick={() => { setModalView('factures'); setDetailsModalVisible(true); }}
                    style={{
                      background: '#fff',
                      borderColor: '#d9d9d9',
                      color: '#000'
                    }}
                  >
                    Voir les factures
                  </Button>
                </Card>
              </Col>
            )}
          </Row>

        </>
      )}

      <RibModal
        visible={ribModalVisible}
        onCancel={() => {
          setRibModalVisible(false)
          setMontantRemboursement(0)
        }}
        onConfirm={handleConfirmRib}
        loading={ribModalLoading}
        requireBic={true}
      />

      <Modal
        title={
          <span style={{ color: primaryColor }}>
            <FontAwesomeIcon icon={faWallet} className="me-2" />
            {modalView === 'details' ? 'Détails de mes avoirs' : 'Factures remboursées'}
          </span>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        width={800}
        footer={null}
      >
        {userData && (
          <div>
            {modalView === 'details' && (
              <>
                {userData.avoirsDetails.pending.length > 0 && (
                  <>
                    <Divider orientation="left">
                      <span style={{ color: secondaryColor }}>
                        Avoirs disponibles ({userData.countPending})
                      </span>
                    </Divider>
                    <List
                      dataSource={userData.avoirsDetails.pending}
                      renderItem={(avoir: CreditNoteType) => (
                        <List.Item
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#f0f7ff',
                            marginBottom: '8px',
                            borderRadius: '8px'
                          }}
                        >
                          <List.Item.Meta
                            title={
                              <Text strong style={{ fontSize: '16px', color: secondaryColor }}>
                                {Number(avoir.montant_avoir).toFixed(2)} €
                              </Text>
                            }
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Dossier: {avoir.annonce?.numDossier || 'N/A'}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Annonce: {avoir.annonce?.numAnnonce || 'N/A'}
                                </Text>
                                {avoir.applied_percentage != null && (
                                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                    Taux appliqué: {Number(avoir.applied_percentage).toFixed(2)}%
                                  </Text>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </>
                )}

                {userData.avoirsDetails.aRembourser.length > 0 && (
                  <>
                    <Divider orientation="left">
                      <span style={{ color: accentColor, fontSize: '16px' }}>
                        En cours de remboursement ({userData.countARembourser})
                      </span>
                    </Divider>
                    <List
                      dataSource={userData.avoirsDetails.aRembourser}
                      renderItem={(avoir: CreditNoteType) => (
                        <List.Item
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#fff7e6',
                            marginBottom: '8px',
                            borderRadius: '8px'
                          }}
                        >
                          <List.Item.Meta
                            title={
                              <Text strong style={{ fontSize: '16px', color: accentColor }}>
                                {Number(avoir.montant_avoir).toFixed(2)} €
                              </Text>
                            }
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Dossier: {avoir.annonce?.numDossier || 'N/A'}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Annonce: {avoir.annonce?.numAnnonce || 'N/A'}
                                </Text>
                                {avoir.applied_percentage != null && (
                                  <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                    Taux appliqué: {Number(avoir.applied_percentage).toFixed(2)}%
                                  </Text>
                                )}
                                <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                  Remboursement sous 30 jours
                                </Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </>
                )}

                {userData.avoirsDetails.pending.length === 0 && userData.avoirsDetails.aRembourser.length === 0 && (
                  <Empty description="Aucun détail disponible" />
                )}
              </>
            )}

            {modalView === 'factures' && (
              <>
                {userData.avoirsDetails.rembourse.length > 0 ? (
                  <>
                    <Divider orientation="left">
                      <span style={{ color: primaryColor }}>
                        Remboursés ({userData.countRembourse})
                      </span>
                    </Divider>
                    <List
                      dataSource={userData.avoirsDetails.rembourse}
                      renderItem={(avoir: CreditNoteType) => (
                        <List.Item
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#f6ffed',
                            marginBottom: '8px',
                            borderRadius: '8px'
                          }}
                          actions={[
                            <Button
                              key="view"
                              size="small"
                              icon={<FontAwesomeIcon icon={faEye} />}
                              onClick={() => {
                                openFactureInNewTab(avoir.id)
                                toast.info("Ouverture de la facture...")
                              }}
                              style={{ color: primaryColor, borderColor: primaryColor }}
                            >
                              Voir
                            </Button>,
                            <Button
                              key="download"
                              size="small"
                              type="primary"
                              icon={<FontAwesomeIcon icon={faDownload} />}
                              onClick={() => {
                                downloadFacture(avoir.id)
                                toast.success("Téléchargement en cours...")
                              }}
                              style={{ backgroundColor: secondaryColor, borderColor: secondaryColor }}
                            >
                              Télécharger
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Text strong style={{ fontSize: '16px', color: primaryColor }}>
                                {Number(avoir.montant_avoir).toFixed(2)} €
                              </Text>
                            }
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Dossier: {avoir.annonce?.numDossier || 'N/A'}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Annonce: {avoir.annonce?.numAnnonce || 'N/A'}
                                </Text>
                                {avoir.applied_percentage != null && (
                                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                    Taux appliqué: {Number(avoir.applied_percentage).toFixed(2)}%
                                  </Text>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </>
                ) : (
                  <Empty description="Aucun avoir remboursé" />
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CreditNote